import React from 'react'
import { IonCol, IonGrid, IonLabel, IonRow } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { DiagnosisConstants } from '../../../../diagnosis/store/diagnosis.constants'
import { IOptionGroup, IQaChartSummaryElement } from '../../store/qaAddendum.types'
import {
  formatValue,
  resolveElementValue,
  isContainerElement,
  getValueRecords,
  isListElement,
  formatRecordLabel,
  isPnnvElement,
  getPnnvRowLabel,
  extractOptionGroups,
} from '../../../utils/qaAddendumChartSummary.util'

const SEGMENT_LABEL_KEYS: Record<string, string> = {
  [DiagnosisConstants.PRIMARY_SEGMENT_ID]: 'diagnosis.primary',
  [DiagnosisConstants.SECONDARY_SEGMENT_ID]: 'diagnosis.secondary',
  [DiagnosisConstants.OTHER_SEGMENT_ID]: 'diagnosis.other',
  [DiagnosisConstants.NOT_PRESENT_SEGMENT_ID]: 'diagnosis.notPresent',
}

interface IChartSummaryElementsProps {
  elements: IQaChartSummaryElement[]
  keyPrefix: string
  sectionHeader?: string
  showOptionIds?: boolean
}

const normalizeContainerLabel = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/\s+list$/i, '')
    .replace(/s$/, '')
    .trim()

const ChartSummaryElements: React.FC<IChartSummaryElementsProps> = ({
  elements,
  keyPrefix,
  sectionHeader,
  showOptionIds = false,
}) => {
  const { t } = useTranslation()

  const duplicatesSectionHeader = (containerLabel: string): boolean =>
    Boolean(sectionHeader) && normalizeContainerLabel(containerLabel) === normalizeContainerLabel(sectionHeader ?? '')

  const renderElementRow = (
    element: IQaChartSummaryElement,
    rowKeyPrefix: string,
    siblings: IQaChartSummaryElement[],
    labelOverride?: string
  ) => {
    const label = labelOverride ?? element.label?.trim()
    const displayValue = labelOverride
      ? formatValue(element.value, showOptionIds)
      : resolveElementValue(element, siblings, showOptionIds)
    const isEmptyPtid = label?.trim().toLowerCase() === 'ptid' && !displayValue

    if ((!label && !displayValue) || isEmptyPtid) return null

    return (
      <IonRow
        key={`${rowKeyPrefix}-${element.id}-${element.binding}`}
        className='qa-addendum-detail-row qa-addendum-chart-summary-inline-row'>
        <IonCol size='12'>
          {label && <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{label}: </IonLabel>}
          <IonLabel className='qa-addendum-detail-row-value fw-400 font-size-16'>
            {displayValue || t('qaFlags.qaAddendum.flagResolution.chartSummaryNoValue')}
          </IonLabel>
        </IonCol>
      </IonRow>
    )
  }

  const renderContainerHeader = (label: string, headerKey: string) => (
    <div key={headerKey} className='qa-addendum-chart-summary-container-header'>
      <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{label}: </IonLabel>
    </div>
  )

  const resolveGroupLabel = (group: IOptionGroup): string => {
    if (group.inlineName) return group.inlineName
    const key = SEGMENT_LABEL_KEYS[group.id]
    return key ? t(key) : group.id
  }

  const renderGroupRow = (group: IOptionGroup, rowKeyPrefix: string, element: IQaChartSummaryElement) => {
    const label = resolveGroupLabel(group)
    return (
      <IonRow
        key={`${rowKeyPrefix}-${element.id}-${element.binding}-${group.id}`}
        className='qa-addendum-detail-row qa-addendum-chart-summary-inline-row'>
        <IonCol size='12'>
          {label && <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{label}: </IonLabel>}
          <IonLabel className='qa-addendum-detail-row-value fw-400 font-size-16'>{group.names.join(', ')}</IonLabel>
        </IonCol>
      </IonRow>
    )
  }

  const renderElements = (
    sectionElements: IQaChartSummaryElement[],
    elementKeyPrefix: string,
    isTopLevel = false
  ): React.ReactNode[] =>
    sectionElements.flatMap((element) => {
      if (isContainerElement(element)) {
        const records = getValueRecords(element.value)
        const containerLabel = element.label?.trim()
        const showRecordLabels = isListElement(element) && records.length > 1
        const showContainerHeader =
          Boolean(containerLabel) && !(isTopLevel && duplicatesSectionHeader(containerLabel ?? ''))

        if (isTopLevel && !showContainerHeader && !showRecordLabels) {
          return records.flatMap((record) => renderElements(record.elements, `${elementKeyPrefix}-${record.recordKey}`))
        }

        const renderedRecords = records
          .map((record, index) => {
            const renderedRecordElements = renderElements(record.elements, `${elementKeyPrefix}-${record.recordKey}`)
            if (renderedRecordElements.length === 0) return null

            const recordLabel = showRecordLabels ? formatRecordLabel(containerLabel, index + 1) : null

            return (
              <div
                key={`${elementKeyPrefix}-${element.binding}-${record.recordKey}`}
                className={`qa-addendum-chart-summary-record${
                  showRecordLabels ? ' qa-addendum-chart-summary-record--numbered' : ''
                }`}
                data-testid={`QaChartSummaryRecord-${record.recordKey}`}>
                {recordLabel ? (
                  <div className='qa-addendum-chart-summary-record-header'>
                    <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{recordLabel}</IonLabel>
                  </div>
                ) : null}
                <div className='qa-addendum-chart-summary-record-fields'>
                  <IonGrid className='ion-no-padding'>{renderedRecordElements}</IonGrid>
                </div>
              </div>
            )
          })
          .filter((record): record is React.ReactElement => record != null)

        if (renderedRecords.length === 0) return []

        return [
          <div
            key={`${elementKeyPrefix}-${element.id}-${element.binding}-container`}
            className='qa-addendum-chart-summary-container'
            data-testid={`QaChartSummaryContainer-${element.binding}`}>
            {showContainerHeader && containerLabel
              ? renderContainerHeader(containerLabel, `${elementKeyPrefix}-${element.binding}-container-header`)
              : null}
            {renderedRecords}
          </div>,
        ]
      }

      if (isPnnvElement(element)) {
        const pnnvLabel = getPnnvRowLabel(element, sectionElements)
        if (!pnnvLabel) return []

        const pnnvRow = renderElementRow(element, elementKeyPrefix, sectionElements, pnnvLabel)
        return pnnvRow ? [pnnvRow] : []
      }

      const optionGroups = extractOptionGroups(element.value, showOptionIds)?.filter((group) => group.names.length > 0)
      if (optionGroups && optionGroups.length > 0) {
        return optionGroups.map((group) => renderGroupRow(group, elementKeyPrefix, element))
      }

      const row = renderElementRow(element, elementKeyPrefix, sectionElements)
      return row ? [row] : []
    })

  const renderedElements = renderElements(elements, keyPrefix, true)
  if (renderedElements.length === 0) return null

  return <IonGrid className='ion-no-padding qa-addendum-chart-summary-section-fields'>{renderedElements}</IonGrid>
}

export default ChartSummaryElements
